/**
 * EditableMatrixName Component
 * Editable breadcrumb header for the matrix page
 */

import { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Check, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const EditableMatrixName = memo(({
  initialName,
  spaceName,
  projectId,
  onNameChange,
  isStackView,
  stackId,
  stackName
}) => {
  const [matrixName, setMatrixName] = useState(initialName);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(initialName);
  const navigate = useNavigate();

  // Update when initialName changes from parent
  useEffect(() => {
    setMatrixName(initialName);
    setTempName(initialName);
  }, [initialName]);

  const handleSave = async (newName) => {
    const trimmedName = newName.trim() || matrixName;

    // Only save if changed
    if (trimmedName === matrixName) {
      setIsEditingName(false);
      return;
    }

    setMatrixName(trimmedName);
    setIsEditingName(false);

    // Save to database
    try {
      const { error } = await supabase
        .from('projects')
        .update({ matrix_name: trimmedName })
        .eq('id', projectId);

      if (error) throw error;
      toast.success('Matrix name updated');
      if (onNameChange) onNameChange(trimmedName);
    } catch (error) {
      console.error('Error saving matrix name:', error);
      toast.error('Failed to save matrix name');
    }
  };

  return (
    <div className="flex items-center">
      {/* Breadcrumb: Stack Name / Matrix Name OR Space Name / Matrix Name */}
      <div className="flex items-center gap-2.5">
        {isStackView && stackId !== 'global' ? (
          <>
            <button
              onClick={() => navigate(`/stack/${stackId}`)}
              className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              {stackName}
            </button>
            <span className="text-sm text-gray-300">/</span>
          </>
        ) : !isStackView ? (
          <>
            <button
              onClick={() => navigate(`/space/${projectId}`)}
              className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              {spaceName}
            </button>
            <span className="text-sm text-gray-300">/</span>
          </>
        ) : null}
        {isStackView && stackId === 'global' ? (
          <h1 className="text-sm font-semibold text-gray-700 px-2 py-1 -ml-2">{matrixName}</h1>
        ) : isEditingName ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave(tempName);
            }}
            className="flex items-center gap-2"
          >
            <input
              type="text"
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              className="h-7 px-2 text-sm font-medium w-48 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setTempName(matrixName);
                  setIsEditingName(false);
                }
              }}
            />
            <Button
              type="submit"
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 hover:bg-gray-100"
            >
              <Check className="h-3.5 w-3.5 text-green-600" />
            </Button>
          </form>
        ) : (
          <button
            onClick={() => {
              setTempName(matrixName);
              setIsEditingName(true);
            }}
            className="group flex items-center gap-2 hover:bg-gray-50 rounded-lg px-2 py-1 -ml-2 transition-all"
          >
            <h1 className="text-sm font-semibold text-gray-700">{matrixName}</h1>
            <Edit2 className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        )}
      </div>
    </div>
  );
});

EditableMatrixName.displayName = 'EditableMatrixName';

export default EditableMatrixName;
